import React, { Component } from "react";
import { Trash2 } from "lucide-react";
import { Column } from "primereact/column";
import { toastService } from "../../services/toastService";
import { DataTable } from "primereact/datatable";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { AppBadge, DataTableToolbar } from "../../components/ui";
import { DeviceStatus, UserDevice } from "../../models/UserDevice";
import { userDevicesApiService } from "../../services/api/users/UserDevicesApiService";
import { dateFilterTemplate, numberFilterMatchModeOptions, paginatorTemplate, stringFilterMatchModeOptions } from "../Paginator";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { utils } from "../../utils/utils";

interface UserDevicesState {
    isLoading: boolean;
    devices: UserDevice[];
}

interface UserDevicesProps {
    userId: number;
}

export default class UserDevicesComponent extends Component<UserDevicesProps, UserDevicesState> {
    deviceStatusClassesMap: Map<DeviceStatus, string> = new Map<DeviceStatus, string>([
        [DeviceStatus.Active, "success"],
        [DeviceStatus.Inactive, "warning"],
        [DeviceStatus.Deleted, "danger"],
    ]);

    private dataTable: DataTable<UserDevice[]> | null = null;

    constructor(props: any | Readonly<any>) {
        super(props);

        this.state = {
            isLoading: false,
            devices: [],
        };
    }

    componentDidMount(): void {
        this.setState({ isLoading: true });
        userDevicesApiService
            .getAll(this.props.userId)
            .then((response) => {
                if (!response.success) {
                    this.setState({ isLoading: false });
                    toastService.error(response.message);
                    return;
                }
                this.setState({ isLoading: false, devices: response.data });
            })
            .catch((err) => {
                this.setState({ isLoading: false });
                toastService.error(err.message);
            });
    }

    actionBodyTemplate = (rowData: UserDevice) => {
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.onRemoveDevice(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                title="Remove Device"
            >
                <Trash2 size={15} />
            </button>
        );
    };

    onRemoveDevice = (rowData: UserDevice) => {
        this.setState({ devices: this.state.devices.filter((device) => device.deviceFingerprint != rowData.deviceFingerprint) });
    };

    renderStatusBadge = (status: DeviceStatus) => {
        const className = this.deviceStatusClassesMap.get(status);
        return <AppBadge variant={className as any}>{DeviceStatus[status]}</AppBadge>;
    };

    renderHeader = () => <DataTableToolbar onClear={() => this.dataTable!.reset()} />;

    onChange = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);
    };

    render() {
        return (
            <div>
                <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 px-5 pt-4 pb-2">Devices</h4>
                            <DataTable
                                ref={(reference) => (this.dataTable = reference)}
                                value={this.state.devices}
                                header={this.renderHeader}
                                loading={this.state.isLoading}
                                emptyMessage="No Devices available"
                                paginator
                                paginatorClassName="p-jc-end"
                                paginatorTemplate={paginatorTemplate}
                                rows={10}
                                sortField="lastUsed"
                                sortOrder={SortOrderType.Descending} //1 for ascending order and -1 for descending order
                                // onFilter={(event) => OnPageChange(this, event, this.onChange)}
                                // onSort={(event) => OnPageChange(this, event, this.onChange)}
                            >
                                <Column
                                    field="ipAddress"
                                    header="Ip Address"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={numberFilterMatchModeOptions}
                                    sortable={numberFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="platform"
                                    header="Platform"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={stringFilterMatchModeOptions}
                                    sortable={stringFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="os"
                                    header="Device OS"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={stringFilterMatchModeOptions}
                                    sortable={stringFilterMatchModeOptions}
                                    body={(item: UserDevice) => `${item.os} ${item.osVersion}`}
                                ></Column>
                                <Column
                                    field="name"
                                    header="Device Name"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={stringFilterMatchModeOptions}
                                    sortable={stringFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="browserName"
                                    header="Browser Name"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={stringFilterMatchModeOptions}
                                    sortable={stringFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="browserVersion"
                                    header="Browser Version"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={numberFilterMatchModeOptions}
                                    sortable={numberFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="vendor"
                                    header="Vendor"
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={stringFilterMatchModeOptions}
                                    sortable={stringFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="registrationDate"
                                    header="Start Using Date"
                                    body={dateBodyTemplate("registrationDate")}
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={numberFilterMatchModeOptions}
                                    sortable={numberFilterMatchModeOptions}
                                    dataType="date"
                                    filterElement={dateFilterTemplate}
                                ></Column>
                                <Column
                                    field="lastUsed"
                                    header="Last Used"
                                    body={dateBodyTemplate("lastUsed")}
                                    showFilterOperator={false}
                                    showAddButton={false}
                                    filter={numberFilterMatchModeOptions}
                                    sortable={numberFilterMatchModeOptions}
                                    dataType="date"
                                    filterElement={dateFilterTemplate}
                                ></Column>
                                <Column
                                    field="status"
                                    header="Status"
                                    body={(rowData: UserDevice) => this.renderStatusBadge(rowData.deviceStatus)}
                                ></Column>
                                {/* <Column body={this.actionBodyTemplate}></Column> */}
                            </DataTable>
            </div>
        );
    }
}
// browserName: string;
// browserVersion: string;
